import { useState, useEffect, useCallback } from 'react';
import { X, Folder, FileText, ChevronRight, ArrowLeft, Loader } from 'lucide-react';
import { listItems, type DriveItem } from '../utils/sharepointPicker.ts';

interface BreadcrumbEntry {
  id?: string;
  name: string;
}

interface Props {
  onPick: (url: string, name: string) => void;
  onClose: () => void;
}

export default function SharePointBrowser({ onPick, onClose }: Props) {
  const [items, setItems] = useState<DriveItem[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFolder = useCallback(async (folderId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listItems(folderId);
      const sorted = [
        ...data.filter(i => i.isFolder).sort((a, b) => a.name.localeCompare(b.name)),
        ...data.filter(i => !i.isFolder).sort((a, b) => a.name.localeCompare(b.name)),
      ];
      setItems(sorted);
    } catch (e: any) {
      setError(e?.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, []);

  // On open: load root, find Q-Pulse folder, navigate straight into it
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        const root = await listItems();
        const qPulse = root.find(i => i.isFolder && i.name === 'Q-Pulse');
        if (qPulse) {
          setBreadcrumb([{ name: 'Q-Pulse', id: qPulse.id }]);
          await loadFolder(qPulse.id);
        } else {
          // Fallback: stay at root if Q-Pulse not found
          setBreadcrumb([{ name: 'Shared Documents' }]);
          const sorted = [
            ...root.filter(i => i.isFolder).sort((a, b) => a.name.localeCompare(b.name)),
            ...root.filter(i => !i.isFolder).sort((a, b) => a.name.localeCompare(b.name)),
          ];
          setItems(sorted);
          setLoading(false);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load files');
        setLoading(false);
      }
    };
    init();
  }, [loadFolder]);

  const openFolder = (item: DriveItem) => {
    setBreadcrumb(prev => [...prev, { id: item.id, name: item.name }]);
    loadFolder(item.id);
  };

  const navigateTo = (index: number) => {
    const entry = breadcrumb[index];
    setBreadcrumb(prev => prev.slice(0, index + 1));
    loadFolder(entry.id);
  };

  const currentFolder = breadcrumb[breadcrumb.length - 1];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-1.5 text-sm text-gray-600 flex-wrap min-w-0">
            {breadcrumb.map((entry, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                <button
                  type="button"
                  onClick={() => navigateTo(i)}
                  className={`hover:text-blue-600 transition-colors truncate max-w-[160px] ${
                    i === breadcrumb.length - 1 ? 'font-semibold text-gray-900' : ''
                  }`}
                >
                  {entry.name}
                </button>
              </span>
            ))}
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-3 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Back button */}
        {breadcrumb.length > 1 && (
          <div className="px-5 pt-3">
            <button
              type="button"
              onClick={() => navigateTo(breadcrumb.length - 2)}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
              <Loader className="w-5 h-5 animate-spin" /> Loading...
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 text-sm font-medium mb-2">{error}</p>
              <button
                type="button"
                onClick={() => loadFolder(currentFolder.id)}
                className="text-xs text-blue-600 hover:underline"
              >
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">This folder is empty</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map(item => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 py-2.5 hover:bg-gray-50 rounded-lg px-2 -mx-2 cursor-pointer transition-colors group"
                  onClick={() => item.isFolder ? openFolder(item) : onPick(item.webUrl, item.name)}
                >
                  {item.isFolder
                    ? <Folder className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    : <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  }
                  <span className="text-sm text-gray-800 flex-1 min-w-0 truncate">{item.name}</span>
                  {item.isFolder
                    ? <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    : <span className="text-xs text-blue-600 font-medium opacity-0 group-hover:opacity-100 flex-shrink-0">Select</span>
                  }
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
          Click a file to select it, or a folder to navigate into it.
        </div>
      </div>
    </div>
  );
}
